import { memo, useEffect, useRef, useState, Fragment } from 'react'
import { createPortal } from 'react-dom'
import { useUser, useAuth } from '@clerk/clerk-react'
import { initOutline } from '../outline/outline.js'
import { loadCurrentUserSeat, provisionFromInvite } from '../services/tenancy.js'
import { setActivityContextProvider } from '../services/activity.js'
import { wf } from '../outline/state.js'
import { initDebugGate } from '../outline/debugOverlay.js'
import { startStuckDetector } from '../outline/stuckDetector.js'   // diagnostic, temporary — see that file
import staticHtml from '../outline/static.html?raw'
import '../outline/outline.css'
import ProjectListSection from './ProjectListSection.jsx'
import ReferenceSection from './ReferenceSection.jsx'
import TeamSection from './TeamSection.jsx'
import MeetingsSection from './MeetingsSection.jsx'
import ReferenceDocumentsSection from './ReferenceDocumentsSection.jsx'
import ReferenceDocumentModal from './ReferenceDocumentModal.jsx'
import ScheduleSection from './ScheduleSection.jsx'
import ThreadsSection from './ThreadsSection.jsx'
import DesignSection from './DesignSection.jsx'
import ParcelDetailSection from './ParcelDetailSection.jsx'
import ParcelResearchTool from './ParcelResearchTool.jsx'
import DuplicateParcelModal from './DuplicateParcelModal.jsx'
import AddParcelModal from './AddParcelModal.jsx'
import AddHistoricalPermitModal from './AddHistoricalPermitModal.jsx'
import PreDesignReportHost from './PreDesignReportHost.jsx'
import OpcReportHost from './OpcReportHost.jsx'
import PunchListPdfHost from './PunchListPdfHost.jsx'
import UpdateBanner from './UpdateBanner.jsx'
import ProductionSection from './ProductionSection.jsx'
import PermitsSection from './PermitsSection.jsx'
import ConstructionSection from './ConstructionSection.jsx'
import MilestoneSections from './MilestoneSections.jsx'
import CategoryZoom from './CategoryZoom.jsx'
import ItemDetailPanel from './ItemDetailPanel.jsx'
import SearchResults from './SearchResults.jsx'
import ZoomTitle from './ZoomTitle.jsx'
import SiteNav from './SiteNav.jsx'
import BillingWorkspace from './BillingWorkspace.jsx'
import ScheduleIntelWorkspace from './ScheduleIntelWorkspace.jsx'
import TeamOverviewWorkspace from './TeamOverviewWorkspace.jsx'
import PostMilestoneModal from './PostMilestoneModal.jsx'
import MoveToPhaseModal from './MoveToPhaseModal.jsx'
import CloseoutModal from './CloseoutModal.jsx'
import AddMilestoneWizard from './AddMilestoneWizard.jsx'
import AddPhaseModal from './AddPhaseModal.jsx'
import CommandBar from './CommandBar.jsx'
import SplitPane from './SplitPane.jsx'
import AdminModal from './AdminModal.jsx'
import WbsTemplateEditor from './WbsTemplateEditor.jsx'
import RoomTemplatesEditor from './admin/RoomTemplatesEditor.jsx'
import ControlBudgetModelsEditor from './admin/ControlBudgetModelsEditor.jsx'
import SpecLibraryModal from './SpecLibraryModal.jsx'
import ScheduleLibraryModal from './ScheduleLibraryModal.jsx'
import ProjectInfoModal from './ProjectInfoModal.jsx'
import PutProjectOnHoldModal from './PutProjectOnHoldModal.jsx'
import CloseOutProjectModal from './CloseOutProjectModal.jsx'
import NotificationBell from './NotificationBell.jsx'
import GlobalSlackPanel from './GlobalSlackPanel.jsx'
import ThreadActivityModal from './ThreadActivityModal.jsx'
import GanttModal from './GanttModal.jsx'
import SiteAnalysisHost from './SiteAnalysisModal.jsx'
import ProgramBuilderHost from './ProgramBuilderModal.jsx'
import FieldObservationHost from './FieldObservationModal.jsx'
import LogPermitActivityModal from './LogPermitActivityModal.jsx'
import PlanCheckLetterModal from './PlanCheckLetterModal.jsx'
import NeighborLetterModal from './NeighborLetterModal.jsx'
import PermitReportCardModal from './PermitReportCardModal.jsx'
import { ReviewClockModal } from './ReviewClock.jsx'
import AddRevisionModal from './AddRevisionModal.jsx'
import AddPermitModal from './AddPermitModal.jsx'
import AddConceptModal from './AddConceptModal.jsx'
import CopyConceptModal from './CopyConceptModal.jsx'
import SetUpBimSystemsModal from './SetUpBimSystemsModal.jsx'
import AddBimElementsModal from './AddBimElementsModal.jsx'
import ConceptDrawingHost from './ConceptDrawingModal.jsx'
import { ProgramModelHost } from './program/ProgramModelModal.jsx'
import AddAgencyReviewModal from './AddAgencyReviewModal.jsx'
import SendJourneyAccessModal from './SendJourneyAccessModal.jsx'
import ConsultantHubSection from './ConsultantHubSection.jsx'
import ConsultantHubAccessModal from './ConsultantHubAccessModal.jsx'
import AddBidCampaignModal from './AddBidCampaignModal.jsx'
import AddBidModal from './AddBidModal.jsx'
import AddBidQuestionModal from './AddBidQuestionModal.jsx'
import PortalModal from './PortalModal.jsx'
import BidLevelingModal from './BidLevelingModal.jsx'
import AddVeEffortModal from './AddVeEffortModal.jsx'
import AddVeItemModal from './AddVeItemModal.jsx'
import AddContractModal from './AddContractModal.jsx'
import ContractorPortalLinkModal from './ContractorPortalLinkModal.jsx'
import AddProposalRequestModal from './AddProposalRequestModal.jsx'
import AddRfiModal from './AddRfiModal.jsx'
import AddSubmittalModal from './AddSubmittalModal.jsx'
import LogSubmittalReviewModal from './LogSubmittalReviewModal.jsx'
import LogResubmittalModal from './LogResubmittalModal.jsx'
import SubmittalReviewModal from './SubmittalReviewModal.jsx'
import AddAsiModal from './AddAsiModal.jsx'
import UserSettingsModal from './UserSettingsModal.jsx'
import EditAsiMilestoneLinkModal from './EditAsiMilestoneLinkModal.jsx'
import EditThreadActivityModal from './EditThreadActivityModal.jsx'
import { CreateThreadModalHost } from './CreateThreadModal.jsx'
import { MoveThreadsModalHost } from './MoveThreadsModal.jsx'
import { DialogHost } from './Dialog.jsx'
import HearingOutcomeHost from './HearingOutcomeHost.jsx'
import StartAheadHost from './StartAheadHost.jsx'
import SelectionsWalkthrough from './SelectionsWalkthrough.jsx'
import { InspirationBoardHost } from './inspiration/InspirationOutline.jsx'
import TrackerWorkspace from './TrackerWorkspace.jsx'
import TemplatesWorkspace from './TemplatesWorkspace.jsx'
import CrmWorkspace from './CrmWorkspace.jsx'
import StudioWorkspace from './StudioWorkspace.jsx'
import PermittingWorkspace from './PermittingWorkspace.jsx'
import ConstructionHubWorkspace from './ConstructionHubWorkspace.jsx'
import FinanceWorkspace from './FinanceWorkspace.jsx'
import { CrmConvertLeadHost } from './crm/CrmConvertLeadModal.jsx'
import ModalFocusTrap from './ModalFocusTrap.jsx'
import SoccerEasterEgg from './SoccerEasterEgg.jsx'
import AJWalker from './AJWalker.jsx'
import ContactsLauncher from './ContactsLauncher.jsx'
import ExpensesLauncher from './ExpensesLauncher.jsx'
import MyFlightsStrip from './MyFlightsStrip.jsx'
import OnboardingLauncher from './OnboardingLauncher.jsx'
import PresentationLauncher from './PresentationLauncher.jsx'
import { DrbPresentationHost } from './DrbPresentationModal.jsx'
import JourneyAuthorSection from './JourneyAuthorSection.jsx'
import JourneyEntryModal from './JourneyEntryModal.jsx'
import JourneyPreviewOverlay from './JourneyPreviewOverlay.jsx'
import TimeCardModal from './TimeCardModal.jsx'
import TimeReviewModal from './TimeReviewModal.jsx'
import ExpenseReviewModal from './ExpenseReviewModal.jsx'
import TimeRecordsModal from './TimeRecordsModal.jsx'
import TimeGate from './TimeGate.jsx'
import CaptainReportGate from './CaptainReportGate.jsx'
import ProgressGate from './ProgressGate.jsx'
import BooksCloseGate from './BooksCloseGate.jsx'
import TriageGate from './TriageGate.jsx'
import FocusWalkGate from './FocusWalkGate.jsx'
import TodayYouFly from './TodayYouFly.jsx'
import { useGateWake } from './LockoutHost.jsx'
import DeleteMilestoneModal from './DeleteMilestoneModal.jsx'
import RespondRfiModal from './RespondRfiModal.jsx'
import EditMilestoneModal from './EditMilestoneModal.jsx'
import ScheduleSubstitutionModal from './ScheduleSubstitutionModal.jsx'

// Full-bleed workspace overlays that cover the whole screen — the outline tree underneath is
// not visible while one of these is open (see the deferred-init note in the mount effect below).
const FULL_BLEED_WORKSPACES = ['tracker', 'billing', 'pulse', 'team', 'templates', 'crm', 'studio', 'finance', 'permitting', 'construction']

// memo prevents parent re-renders from re-running the effect after the
// outline has taken over its own DOM management
const OutlineView = memo(function OutlineView({ recordId }) {
  const { user } = useUser()
  const { getToken } = useAuth()
  const containerRef = useRef(null)
  const initialized = useRef(false)
  // Wake the daily lockouts when someone returns to a tab left open (idle 30 min, page visible
  // again, or a new day) — a focus event alone often never comes (LockoutHost.jsx).
  useGateWake()
  const [sectionsEl, setSectionsEl] = useState(null)
  const [zoomTitleEl, setZoomTitleEl] = useState(null)
  // Bumped by the /refresh command (ctx.refreshAll dispatches 'aj-force-remount') — keying the
  // portaled section tree on this forces every lazy-loaded section to unmount/remount, resetting
  // the loadedRef/hasLoaded guards described in CLAUDE.md's Lazy Load Rules so they refetch even
  // though cache-clearing alone can't reach a ref that only checks "have I already fetched".
  const [refreshEpoch, setRefreshEpoch] = useState(0)

  useEffect(function() {
    if (!containerRef.current || initialized.current) return
    initialized.current = true

    // user is guaranteed loaded here — parent page gates on isLoaded && isSignedIn
    const email  = user?.primaryEmailAddress?.emailAddress || null
    const userId = user?.id || null

    // Expose Clerk token getter to services/tenancy.js admin write functions.
    // Pattern is consistent with how wf/ctx singletons work in this codebase.
    window.__clerkGetToken = () => getToken()

    // Active-work log (docs/ACTIVITY_HUD_PLAN.md): the outline's zoom state says which project and
    // milestone the person is in; a full-bleed workspace (?ws=) names the tool instead.
    // Most milestone work happens expanded in place, not zoomed, so the milestone last clicked inside
    // counts too, for ten minutes (2026-10-22: the suggestion's "In Cortex" line names milestones).
    const lastMs = window.__ajLastMs || (window.__ajLastMs = { id: null, at: 0 })
    if (!window.__ajLastMsListener) {
      window.__ajLastMsListener = true
      document.addEventListener('pointerdown', (e) => {
        const node = e.target && e.target.closest ? e.target.closest('.wf-node[data-key^="ms:"]') : null
        if (node) { lastMs.id = node.getAttribute('data-key').slice(3); lastMs.at = Date.now() }
      }, true)
    }
    setActivityContextProvider(() => {
      const ws = new URLSearchParams(window.location.search).get('ws')
      const recent = lastMs.id && Date.now() - lastMs.at < 10 * 60000 ? lastMs.id : null
      return {
        tool: ws ? String(ws).toLowerCase().replace(/[^a-z0-9-]+/g, '-').slice(0, 40) : 'outline',
        // A workspace covers the outline, so the project zoomed behind it is not what is being worked on.
        projectId: ws ? null : (wf.zoomProjectId || null),
        milestoneId: wf.zoomMsId || (ws ? null : recent),
        memberId: wf.currentMemberId || null,
      }
    })

    // Dev aid: milestone read-flip parity check. In the console: await __ajMsParity('rec...projectId')
    // compares the Supabase-mapped milestones vs live Airtable and prints field-level diffs.
    window.__ajMsParity = (pid) => import('../services/milestonesParity.js').then(m => m.milestoneParity(pid))

    // Load tenancy seat data in parallel with outline init. Non-blocking —
    // the outline renders normally; tenancy gates (admin link, /admin route)
    // check isSeatLoaded() before rendering gated content.
    //
    // If the user has no seat yet (new invite recipient), try to auto-provision
    // from a pending invite matched by their Clerk email. This is the zero-touch
    // path for invited users — they sign up and are provisioned automatically.
    if (userId) {
      loadCurrentUserSeat(userId).then(async seat => {
        if (!seat) await provisionFromInvite()
        // Restore/clear any persisted /debug state now that the seat (or freshly provisioned
        // seat) is in the cache. The live eligibility check itself (debugOverlay.isEligible)
        // doesn't depend on this having run — it reads the seat cache directly on every call.
        initDebugGate()
      })
    } else {
      initDebugGate()
    }

    containerRef.current.innerHTML = staticHtml
    setSectionsEl(containerRef.current.querySelector('#aj-sc-sections'))
    setZoomTitleEl(containerRef.current.querySelector('#aj-sc-zoom-title'))

    // initOutline eagerly loads the project list + members + team contacts through the outline
    // engine's OWN separate request queue (outline/api.js — no rate throttle, unlike
    // services/_api.js's queue). When a full-bleed workspace (Billing/CRM/etc.) is open at mount,
    // the outline is completely hidden behind it, so this load is pure waste right now AND was the
    // other half of the Billing Run's 429-on-cold-refresh bug: two uncoordinated queues booting at
    // once burst well past Airtable's ~5 req/sec cap. Deferring it a few seconds costs nothing
    // visible (nothing renders from it while hidden) and lets the workspace's own prefetch go first.
    const wsAtMount = new URLSearchParams(window.location.search).get('ws')
    if (FULL_BLEED_WORKSPACES.includes(wsAtMount)) {
      setTimeout(() => initOutline(recordId || null, email), 4000)
    } else {
      initOutline(recordId || null, email)
    }
  }, [])

  // Diagnostic (2026-09-23): record outline sections that get stuck loading or show empty against a
  // non-zero count — Matt sees projects half-populate until a reload. See outline/stuckDetector.js.
  useEffect(() => { startStuckDetector() }, [])

  useEffect(() => {
    function onForceRemount() { setRefreshEpoch(e => e + 1) }
    window.addEventListener('aj-force-remount', onForceRemount)
    return () => window.removeEventListener('aj-force-remount', onForceRemount)
  }, [])

  // Manage document.title — default to "Alder James Projects" when no workspace overlay is active.
  // Workspace components set the title when they become active; this effect resets it on close.
  useEffect(() => {
    const ws = new URLSearchParams(window.location.search).get('ws')
    if (!FULL_BLEED_WORKSPACES.includes(ws)) {
      document.title = 'Alder James Projects'
      if (ws === 'projects') {
        const u = new URL(window.location); u.searchParams.delete('ws')
        window.history.replaceState(null, '', u)
      }
    }
    function onClosed() {
      requestAnimationFrame(() => {
        const p = new URLSearchParams(window.location.search)
        if (!FULL_BLEED_WORKSPACES.includes(p.get('ws'))) document.title = 'Alder James Projects'
      })
    }
    window.addEventListener('close-tracker', onClosed)
    window.addEventListener('close-billing', onClosed)
    window.addEventListener('close-pulse', onClosed)
    window.addEventListener('close-team', onClosed)
    window.addEventListener('close-templates', onClosed)
    window.addEventListener('close-crm', onClosed)
    window.addEventListener('close-studio', onClosed)
    return () => {
      window.removeEventListener('close-tracker', onClosed); window.removeEventListener('close-billing', onClosed)
      window.removeEventListener('close-pulse', onClosed); window.removeEventListener('close-team', onClosed)
      window.removeEventListener('close-templates', onClosed); window.removeEventListener('close-crm', onClosed)
      window.removeEventListener('close-studio', onClosed)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Restore full-page editors from URL on mount (e.g. browser refresh while one was open).
  // Workspaces (tracker/billing) self-restore via their own readActive/readStored URL checks.
  // (ws=spec restore removed — AlderSPEC is a real route now, /spec carries its own context.)
  // (ws=schedule restore removed — the Schedule Builder lives in AlderSPEC's Schedules tab now,
  // a real route that carries its own context.)

  return (
    <>
      <SiteNav />
      <PostMilestoneModal />
      <MoveToPhaseModal />
      <CloseoutModal />
      <AddMilestoneWizard />
      <AddPhaseModal />
      <CommandBar />
      <SplitPane />
      <AdminModal />
      <WbsTemplateEditor />
      <RoomTemplatesEditor />
      <ControlBudgetModelsEditor />
      <SpecLibraryModal />
      <ScheduleLibraryModal />
      <ProjectInfoModal />
      <PutProjectOnHoldModal />
      <CloseOutProjectModal />
      <ThreadActivityModal />
      <GanttModal />
      <SiteAnalysisHost />
      <ProgramBuilderHost />
      <FieldObservationHost />
      <LogPermitActivityModal />
      <PlanCheckLetterModal />
      <NeighborLetterModal />
      <PermitReportCardModal />
      <ReviewClockModal />
      <AddRevisionModal />
      <AddPermitModal />
      <AddConceptModal />
      <CopyConceptModal />
      {/* Opens on 'open-setup-bim-modal' — dispatched by the BIM Systems section ⋯ menu (outline.js). */}
      <SetUpBimSystemsModal />
      {/* Opens on 'open-add-bim-elements-modal' — dispatched by a BimRow's own ⋯ menu (BimSystemList.jsx). */}
      <AddBimElementsModal />
      {/* Opens on 'open-concept-drawing-modal' — dispatched by SketchManager.jsx's PDF upload
          flow (journey Sketch ribbon, authored here inside the main app) as well as the /present
          Sketches wall's own PDF flow when reached from a team member's browser tab. */}
      <ConceptDrawingHost />
      {/* Opens on 'open-program-model-modal' — dispatched by the Concept ⋯ menu (outline.js). */}
      <ProgramModelHost />
      <AddAgencyReviewModal />
      {/* Opens on 'open-send-journey-access-modal' — JourneyAuthorSection.jsx's "Manage access…"
          menu item (per-recipient access, database/134_journey_recipients.sql). */}
      <SendJourneyAccessModal />
      <ConsultantHubAccessModal />
      <AddBidCampaignModal />
      <AddBidModal />
      <AddBidQuestionModal />
      <PortalModal />
      <BidLevelingModal />
      <AddVeEffortModal />
      <AddVeItemModal />
      <AddContractModal />
      <AddRfiModal />
      <AddSubmittalModal />
      <LogSubmittalReviewModal />
      <LogResubmittalModal />
      <SubmittalReviewModal />
      <AddAsiModal />
      <UserSettingsModal />
      <EditAsiMilestoneLinkModal />
      <EditThreadActivityModal />
      <CreateThreadModalHost />
      <MoveThreadsModalHost />
      <ContactsLauncher />
      <ExpensesLauncher />
      {/* The person's own flights, pinned above the outline at every zoom (replaced My Work,
          2026-09-02 — docs/FLOW_RECONCILIATION_PLAN.md decision 9). Portals into #aj-sc-flights. */}
      <MyFlightsStrip />
      <OnboardingLauncher />
      <PresentationLauncher />
      <DrbPresentationHost />
      <JourneyEntryModal />
      <JourneyPreviewOverlay />
      <TimeCardModal />
      <TimeReviewModal />
      <ExpenseReviewModal />
      <TimeRecordsModal />
      <TimeGate />
      <CaptainReportGate />
      <TriageGate />
      <FocusWalkGate />
      <TodayYouFly />
      {/* OnDeckGate (the running-dry lockout) retired 2026-09-27: the Line-up tab's red headline and the
          Flight Board menu count replaced it (LineUpTab.jsx). */}
      <DeleteMilestoneModal />
      <ProgressGate />
      {/* Monday lockout for closing the books; outline only, since Books opens here (docs/BOOKKEEPING.md) */}
      <BooksCloseGate />
      <RespondRfiModal />
      <EditMilestoneModal />
      <ScheduleSubstitutionModal />
      <ContractorPortalLinkModal />
      <AddProposalRequestModal />
      <NotificationBell />
      <GlobalSlackPanel />
      <DialogHost />
      <HearingOutcomeHost />
      <StartAheadHost />
      <SelectionsWalkthrough />
      <InspirationBoardHost />
      <ModalFocusTrap />
      <SoccerEasterEgg />
      <AJWalker />
      <BillingWorkspace />
      <ScheduleIntelWorkspace />
      <TeamOverviewWorkspace />
      <TrackerWorkspace />
      <TemplatesWorkspace />
      <CrmWorkspace />
      <StudioWorkspace />
      <PermittingWorkspace />
      <ConstructionHubWorkspace />
      <FinanceWorkspace />
      <CrmConvertLeadHost />
      <ParcelResearchTool />
      <DuplicateParcelModal />
      <AddParcelModal />
      <AddHistoricalPermitModal />
      <PreDesignReportHost />
      <OpcReportHost />
      <PunchListPdfHost />
      <ReferenceDocumentModal />
      <UpdateBanner />
      <div ref={containerRef} />
      {zoomTitleEl && createPortal(<ZoomTitle />, zoomTitleEl)}
      {sectionsEl && createPortal(
        <Fragment key={refreshEpoch}>
          <ProjectListSection />
          <ScheduleSection />
          <JourneyAuthorSection />
          <ConsultantHubSection />
          <ReferenceSection />
          <TeamSection />
          <MeetingsSection />
          <ThreadsSection />
          <ReferenceDocumentsSection />
          <DesignSection />
          <ParcelDetailSection />
          <ProductionSection />
          <PermitsSection />
          <ConstructionSection />
          <SearchResults />
          <CategoryZoom />
          <ItemDetailPanel />
          <MilestoneSections />
        </Fragment>,
        sectionsEl
      )}
    </>
  )
})

export default OutlineView
